import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isLikelyImageAttachment } from '@utils/chatAttachmentKind.util';

const DEV = typeof import.meta !== 'undefined' && import.meta.env?.DEV;

const previewCache = new Map(); // key(url) -> { kind, data }

function useIsVisible(ref) {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === 'undefined') {
            setVisible(true);
            return;
        }
        const obs = new IntersectionObserver(
            (entries) => {
                const v = entries.some((e) => e.isIntersecting);
                if (v) setVisible(true);
            },
            { root: null, rootMargin: '200px', threshold: 0.01 },
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [ref]);
    return visible;
}

function Skeleton({ dark }) {
    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                background: dark
                    ? 'linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0.10), rgba(255,255,255,0.06))'
                    : 'linear-gradient(90deg, rgba(2,6,23,0.05), rgba(2,6,23,0.10), rgba(2,6,23,0.05))',
                backgroundSize: '200% 100%',
                animation: 'chatPreviewShimmer 1.2s ease-in-out infinite',
            }}
        />
    );
}

function Fallback({ dark, label = 'Preview unavailable' }) {
    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: dark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)',
                fontSize: 12,
                fontWeight: 700,
                padding: 12,
                textAlign: 'center',
            }}
        >
            {label}
        </div>
    );
}

export const ChatAttachmentPreview = React.memo(function ChatAttachmentPreview({ url, name, mimeType, previewThumbnailUrl, dark }) {
    const wrapRef = useRef(null);
    const visible = useIsVisible(wrapRef);

    const isImage = useMemo(
        () => isLikelyImageAttachment({ name, url, mimeType, previewUrl: previewThumbnailUrl }),
        [name, url, mimeType, previewThumbnailUrl],
    );

    const [state, setState] = useState({ status: 'idle', kind: 'fallback', data: null });

    useEffect(() => {
        if (!visible) return;
        if (!url && !previewThumbnailUrl) return;
        const key = String(previewThumbnailUrl || url);
        const cached = previewCache.get(key);
        if (cached) {
            setState({ status: 'ready', kind: cached.kind, data: cached.data });
            return;
        }

        let cancelled = false;
        const run = async () => {
            try {
                setState((s) => (s.status === 'ready' ? s : { status: 'loading', kind: 'skeleton', data: null }));

                // Prefer backend-generated thumbnail.
                if (previewThumbnailUrl) {
                    const payload = { kind: 'image', data: { src: previewThumbnailUrl } };
                    previewCache.set(key, payload);
                    if (!cancelled) setState({ status: 'ready', ...payload });
                    return;
                }

                if (isImage) {
                    const payload = { kind: 'image', data: { src: url } };
                    previewCache.set(key, payload);
                    if (!cancelled) setState({ status: 'ready', ...payload });
                    return;
                }

                const payload = { kind: 'fallback', data: { label: 'Preview unavailable' } };
                previewCache.set(key, payload);
                if (!cancelled) setState({ status: 'ready', ...payload });
            } catch (e) {
                if (DEV) console.debug('[chat] attachment preview failed', { url, name, err: String(e?.message || e) });
                const payload = { kind: 'fallback', data: { label: 'Preview unavailable' } };
                previewCache.set(key, payload);
                if (!cancelled) setState({ status: 'ready', ...payload });
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [visible, url, name, mimeType, previewThumbnailUrl, isImage]);

    return (
        <div
            ref={wrapRef}
            style={{
                width: '100%',
                height: '100%',
                position: 'relative',
            }}
        >
            {state.status === 'loading' || state.kind === 'skeleton' ? <Skeleton dark={dark} /> : null}
            {state.status === 'ready' && state.kind === 'image' ? (
                <img src={state.data?.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            ) : null}
            {state.status === 'ready' && state.kind === 'fallback' ? <Fallback dark={dark} label={state.data?.label} /> : null}
        </div>
    );
});

// local keyframes (inline, no styled-components)
if (typeof document !== 'undefined' && !document.getElementById('chatPreviewShimmerStyle')) {
    const style = document.createElement('style');
    style.id = 'chatPreviewShimmerStyle';
    style.textContent = `
@keyframes chatPreviewShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
`;
    document.head.appendChild(style);
}

